class SamplesDrawer extends HTMLElement {
  constructor() {
    super();

    this.close = this.querySelector('[data-close]');
    this.active = false;
    this.lastValidationTime = 0;
    this.close.addEventListener('click', this.handleActive.bind(this));
    this.overlay = document.querySelector('[data-samples-overlay]');
    // Add event listener to the overlay to close the drawer
    this.overlay.addEventListener('click', this.handleActive.bind(this));
    
    this.setupMobileScrolling();
  }

  handleActive(e) {
    // Get the drawer element, find the parent div and add the class 'samples-drawer--active' 
    // to the parent div
    this.active = !this.active;

    const parentDiv = this.closest('div');
    if (parentDiv) {
      parentDiv.classList.toggle('samples-drawer--active', this.active);
    }
    // Toggle the overlay visibility
    if (this.overlay) {
      this.overlay.classList.toggle('active', this.active);
    }
    
    // If opening the drawer, validate cart state
    if (this.active) {
      this.validateCartOnOpen();
      
      // Also dispatch an event to notify other components
      document.dispatchEvent(new CustomEvent('samples-drawer:opened', {
        detail: { timestamp: Date.now() }
      }));
    }
  } 

  toggleOpen(e) {
    this.active = !this.active;
    const parentDiv = this.closest('div');
    if (parentDiv) {
      parentDiv.classList.toggle('samples-drawer--active', this.active);
    }
    if (this.overlay) {
      this.overlay.classList.toggle('active', this.active);
    }
    
    // If opening the drawer, validate cart state
    if (this.active) {
      this.validateCartOnOpen();
      
      // Also dispatch an event to notify other components
      document.dispatchEvent(new CustomEvent('samples-drawer:opened', {
        detail: { timestamp: Date.now() }
      }));
    }
  }

  async validateCartOnOpen() {
    // Prevent validation if called recently (within 2 seconds)
    const now = Date.now();
    if (now - this.lastValidationTime < 2000) {
      return;
    }
    this.lastValidationTime = now;

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      
      // Check if there are any paid products (non-samples)
      const hasPaidProducts = cart.items.some(item => item.product_type !== 'sample');
      const sampleItems = cart.items.filter(item => item.product_type === 'sample');
      
      console.log('Samples drawer validation:', { 
        hasPaidProducts, 
        sampleCount: sampleItems.length, 
        totalItems: cart.items.length 
      });
      
      if (!hasPaidProducts) {
        // Show warning and disable the form
        this.showNoPaidProductsWarning();
        
        // If there are samples but no paid products, remove them
        if (sampleItems.length > 0) {
          console.log('Removing samples due to no paid products in cart');
          await this.removeSamplesFromCart();
        }
      } else {
        // Hide warning and enable the form
        this.hideNoPaidProductsWarning();
      }
    } catch (e) {
      console.error('Failed to validate cart on drawer open:', e);
    }
  }

  async removeSamplesFromCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      
      const sampleItems = cart.items.filter(item => item.product_type === 'sample');
      
      if (sampleItems.length === 0) return;
      
      // Create updates object to remove all samples
      const updates = {};
      sampleItems.forEach(item => {
        updates[item.variant_id] = 0;
      });
      
      // Remove all samples
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });
      
      if (response.ok) {
        console.log('Samples removed from cart via drawer validation');
        
        // Dispatch event to notify other components
        document.dispatchEvent(new CustomEvent('samples:removed', {
          detail: { reason: 'drawer_validation', source: 'samples-drawer' }
        }));
        
        // Refresh the cart drawer if it has a refresh method
        const cartItems = document.querySelector('cart-items');
        if (cartItems && typeof cartItems.getCart === 'function') {
          cartItems.getCart();
        }
      }
    } catch (e) {
      console.error('Failed to remove samples from cart:', e);
    }
  }

  showNoPaidProductsWarning() {
    // Find or create warning message
    let warning = this.querySelector('.samples-drawer__no-paid-products-warning');
    if (!warning) {
      warning = document.createElement('div');
      warning.className = 'samples-drawer__no-paid-products-warning';
      warning.innerHTML = `
        <div class="samples-drawer__warning">
          <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-alert" viewBox="0 0 13 13" width="13" height="13">
            <circle cx="6.5" cy="6.50049" r="5.5" stroke="white" stroke-width="2"/>
            <circle cx="6.5" cy="6.5" r="5.5" fill="#FF6B35" stroke="#FF6B35" stroke-width="0.7"/>
            <path d="M5.87413 3.52832L5.97439 7.57216H7.02713L7.12739 3.52832H5.87413ZM6.50076 9.66091C6.88091 9.66091 7.18169 9.37267 7.18169 9.00504C7.18169 8.63742 6.88091 8.34917 6.50076 8.34917C6.12061 8.34917 5.81982 8.63742 5.81982 9.00504C5.81982 9.37267 6.12061 9.66091 6.50076 9.66091Z" fill="white"/>
          </svg>
          <span>Add a paid product to your cart to select samples</span>
        </div>
      `;
      
      // Insert after header
      const header = this.querySelector('.samples-drawer__header');
      if (header) {
        header.insertAdjacentElement('afterend', warning);
      }
    }
    
    warning.style.display = 'block';
    
    // Disable the form
    const form = this.querySelector('samples-form');
    if (form) {
      form.style.opacity = '0.5';
      form.style.pointerEvents = 'none';
    }
  }

  hideNoPaidProductsWarning() {
    const warning = this.querySelector('.samples-drawer__no-paid-products-warning');
    if (warning) {
      warning.style.display = 'none';
    }
    
    // Enable the form
    const form = this.querySelector('samples-form');
    if (form) {
      form.style.opacity = '1';
      form.style.pointerEvents = 'auto';
    }
  }

  setupMobileScrolling() {
    const productsContainer = this.querySelector('.samples-drawer__products');
    if (!productsContainer) return;

    if ('ontouchstart' in window) {
      productsContainer.addEventListener('touchstart', (e) => {
        e.stopPropagation();
      }, { passive: true });

      productsContainer.addEventListener('touchmove', (e) => {
        e.stopPropagation();
      }, { passive: true });

      productsContainer.style.webkitOverflowScrolling = 'touch';
      productsContainer.style.overflowY = 'auto';
    }
  }

}

if (!customElements.get('samples-drawer')) {
  customElements.define('samples-drawer', SamplesDrawer);
}
